// Import pre-boot script first to ensure configuration is ready
import "@/lib/pre-boot";

import { createRoot } from "react-dom/client";
import App from "./App";
import "./index.css";
import { QueryClientProvider } from "@tanstack/react-query";
import { queryClient } from "@/lib/queryClient";
import { AuthProvider } from "@/hooks/use-auth";
import { Toaster } from "@/components/ui/toaster";
import { TooltipProvider } from "@/components/ui/tooltip";
import { initReplitEnvironment } from "@/lib/replit-utils";
import { logDeploymentInfo } from "@/lib/env-debug";

// PWA Service Worker Registration
import { registerSW } from 'virtual:pwa-register';

// Ensure React is properly initialized
if (typeof window !== 'undefined') {
  // Output environment debug information
  logDeploymentInfo();
  
  // Initialize Replit environment fixes for Vite host restrictions
  initReplitEnvironment();

  // Register service worker for PWA functionality
  const updateSW = registerSW({
    onNeedRefresh() {
      if (confirm('New content available. Reload to update?')) {
        updateSW(true);
      }
    },
    onOfflineReady() {
      console.log('StayMiddle is ready to work offline');
      // Optional: Show a toast notification
      // toast({ title: "App ready for offline use" });
    },
    onRegistered(r) {
      console.log('SW Registered: ' + r);
    },
    onRegisterError(error) {
      console.log('SW registration error', error);
    },
  });
}

// Check for Firebase API key issues, but don't exit since we have fallbacks
if (!import.meta.env.VITE_FIREBASE_API_KEY && import.meta.env.PROD) {
  console.warn("Warning: Missing VITE_FIREBASE_API_KEY environment variable. Using fallback mechanisms.");
  
  // If we're on staymidle.com specifically, add a warning class to the root element
  if (typeof window !== 'undefined' && window.location.hostname === 'staymidle.com') {
    const rootElement = document.getElementById("root");
    if (rootElement) {
      rootElement.classList.add('firebase-config-warning');
    }
    
    console.info("Running on staymidle.com with fallback Firebase configuration");
  }
}

// Ensure the root element exists
const rootElement = document.getElementById("root");
if (!rootElement) {
  throw new Error("Root element not found. Make sure there is a div with id 'root' in your index.html");
}

// Create root and render app
const root = createRoot(rootElement);
root.render(
  <QueryClientProvider client={queryClient}>
    <AuthProvider>
      <TooltipProvider>
        <App />
        <Toaster />
      </TooltipProvider>
    </AuthProvider>
  </QueryClientProvider>
);
